import { type ComponentProps, useMemo } from "react"
import { Area, AreaChart, ResponsiveContainer } from "recharts"
import cn from "@/v2-ui/utils/utils.cn"
import Tooltip from "@/v2-ui/tooltip"
import { formatLatency } from "@/v2-backoffice-client/uptime/uptime.utils"
import type { UptimeTargetFragment } from "@/v2-backoffice-client/uptime/__types__/uptime.fragment"

// Number of hourly buckets the sparkline always renders. Matches
// the 24h window the per-target sproc emits; fewer real buckets
// just leave leading nulls so all sparkline cells line up edge-to-
// edge in the table view.
const BUCKET_COUNT = 24

export type UptimeLatencySparklineProps = Omit<
  ComponentProps<"div">,
  "children"
> & {
  history: UptimeTargetFragment["LatencyHourlyHistory"]
  // Optional p95 baseline drawn as a faint dashed reference line.
  // Mirrors the big chart in the view sheet so the sparkline
  // hints at whether the current trend is faster/slower than
  // baseline at a glance.
  baselineP95Ms?: number | null
  // When the row is currently down we tint the sparkline red
  // instead of the standard primary tint, so a visually-empty
  // cell still reads as "this is unhealthy".
  isDown?: boolean
}

type Point = { i: number; avg: number | null; max: number | null }

// Pad a possibly-sparse hourly array out to BUCKET_COUNT slots,
// aligning by BucketStart (ISO timestamps). The sparkline cell is
// fixed-width, so all rows render the same wall-clock window
// regardless of how many buckets a particular target produced.
function padBuckets(
  history: UptimeTargetFragment["LatencyHourlyHistory"],
): Point[] {
  if (!history.length) {
    return Array.from({ length: BUCKET_COUNT }, (_, i) => ({
      i,
      avg: null,
      max: null,
    }))
  }

  // Find the latest bucket and align everything against that, so a
  // target probed only 4h ago still gets its tail anchored on the
  // right edge of the sparkline.
  const sorted = [...history].toSorted((a, b) =>
    a.BucketStart < b.BucketStart ? -1 : 1,
  )
  const latest = new Date(sorted[sorted.length - 1].BucketStart).getTime()
  const byHour = new Map<number, (typeof sorted)[number]>()
  for (const h of sorted) {
    const offset = Math.round(
      (latest - new Date(h.BucketStart).getTime()) / 3_600_000,
    )
    byHour.set(offset, h)
  }

  const out: Point[] = []
  for (let i = 0; i < BUCKET_COUNT; i++) {
    const offset = BUCKET_COUNT - 1 - i
    const h = byHour.get(offset)
    out.push({
      i,
      avg: h?.AvgDurationMs ?? null,
      max: h?.MaxDurationMs ?? null,
    })
  }
  return out
}

// Tooltip summary for the sparkline. Compact -- the cell is small
// so the operator just gets a "what am I looking at" hint, with a
// nudge to open the view sheet for the full chart.
function describe(history: UptimeTargetFragment["LatencyHourlyHistory"]): {
  avg: number | null
  max: number | null
  buckets: number
} {
  let sum = 0
  let count = 0
  let max: number | null = null
  for (const h of history) {
    if (h.AvgDurationMs != null && h.AvgDurationMs > 0) {
      sum += h.AvgDurationMs
      count++
    }
    if (h.MaxDurationMs != null && (max == null || h.MaxDurationMs > max)) {
      max = h.MaxDurationMs
    }
  }
  return {
    avg: count > 0 ? Math.round(sum / count) : null,
    max,
    buckets: history.length,
  }
}

function UptimeLatencySparkline(props: UptimeLatencySparklineProps) {
  const { history, baselineP95Ms, isDown, className, ...divProps } = props

  const data = useMemo(() => padBuckets(history), [history])
  const summary = useMemo(() => describe(history), [history])

  const hasData = data.some((d) => d.avg != null)

  // Empty state: show a flat dashed neutral line so the cell
  // doesn't collapse to a different height than its neighbours.
  if (!hasData) {
    return (
      <div
        className={cn("uptime-latency-sparkline-empty", className)}
        title={
          isDown
            ? "Currently down – no latency samples in the last 24h"
            : "No latency samples in the last 24h"
        }
        {...divProps}
      >
        <span className="text-muted-foreground/50 text-[10px]">no data</span>
      </div>
    )
  }

  // Tint: red while down so the sparkline doubles as a status
  // signal, primary otherwise.
  const stroke = isDown ? "hsl(var(--destructive))" : "hsl(var(--primary))"
  const fill = isDown ? "hsl(var(--destructive))" : "hsl(var(--primary))"

  const tooltipContent = (
    <div className="text-xs space-y-0.5">
      <div className="font-medium">Last 24h latency</div>
      <div>
        avg {summary.avg != null ? formatLatency(summary.avg) : "—"} · max{" "}
        {summary.max != null ? formatLatency(summary.max) : "—"}
      </div>
      {/* Tooltips are inverted (bg-foreground / text-background); a muted
          foreground colour is unreadable on them, so fade instead. */}
      <div className="opacity-70">
        {summary.buckets} hourly bucket{summary.buckets === 1 ? "" : "s"}
        {baselineP95Ms != null && baselineP95Ms > 0 && (
          <> · p95 baseline {formatLatency(baselineP95Ms)}</>
        )}
      </div>
    </div>
  )

  const gradId = isDown ? "uls-fill-down" : "uls-fill"

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <div
          className={cn("uptime-latency-sparkline", className)}
          {...divProps}
        >
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 2, right: 0, bottom: 2, left: 0 }}
            >
              <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={fill} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={fill} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="avg"
                stroke={stroke}
                strokeWidth={1.25}
                strokeOpacity={0.9}
                fill={`url(#${gradId})`}
                isAnimationActive={false}
                connectNulls
                dot={false}
                activeDot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Tooltip.Trigger>
      <Tooltip.Content side="top">{tooltipContent}</Tooltip.Content>
    </Tooltip>
  )
}

export default UptimeLatencySparkline
